import { CUTSCENES, SUPPORTED_LOCALES } from '../constants/Constants';
import { KEYS } from '../constants/SceneConstants';
import { BOSS_MAP } from '../game/EnemyController';

export default class ShortcutController {
  constructor(game) {
    this._game = game;
    this._state = false;
    this.setState('default');
  }

  setState(id) {
    if (this._state === id) return;
    this._game.shortcuts.reset();
    if (show[id]) show[id](this._game);
    else show['default'](this._game);
    addDebug(this._game);
  }
}

ShortcutController.STATES = {
  DEFAULT: 'default',
  INGAME: 'ingame',
  COMBAT_TEST: 'combattest',
  ENEMY_VIEWER: 'enemyviewer',
};

const show = {
  // =============================================================================================
  // DEFAULT SHORTCUTS
  // =============================================================================================

  default: (game) => {
    addLocaleOptions(game);
    addPlayerOptions(game);
    addLaunchTests(game);
    addStageStart(game);
    addRendererOptions(game);
    addMapUploader(game);
  },

  // =============================================================================================
  // IN GAME SHORTCUTS
  // =============================================================================================

  ingame: (game) => {
    addInGameOptions(game);
    addPlayerOptions(game);
    addStageStart(game);
    addExit(game);
  },

  // =============================================================================================
  // ENEMY BROWSER
  // =============================================================================================

  enemyviewer: (game) => {
    game.shortcuts.open();
    game.shortcuts.show();
    game.shortcuts.addShortcutGroup({
      id: 'charactertest',
      title: 'Char Test',
      open: true,
    });
    game.shortcuts.addDropDown({
      group: 'charactertest',
      field: 'enemytest',
      title: 'View Enemy',
      value: ['', ...game.model.getEnemies().map((e) => e.spineid)],
      onChange: (e) => {
        const scene = game.sceneController.getScene(KEYS.EnemyViewer);
        if (scene) scene.changeCharacter(e);
      },
    });
    game.shortcuts.addDropDown({
      group: 'charactertest',
      field: 'enemytest',
      title: 'View Hero',
      value: ['robin', 'beastboy', 'raven', 'starfire', 'cyborg'],
      onChange: (e) => {
        const scene = game.sceneController.getScene(KEYS.EnemyViewer);
        if (scene) scene.changeCharacter(e);
      },
    });
    game.shortcuts.addDropDown({
      group: 'charactertest',
      field: 'bosstest',
      title: 'View Boss',
      value: ['cyberGorilla', 'dragon', 'ultralak', 'sheriff', 'foreman'],
      onChange: (e) => {
        const scene = game.sceneController.getScene(KEYS.EnemyViewer);
        if (scene) scene.changeCharacter(e);
      },
    });
    addExit(game);
  },

  // =============================================================================================
  // COMBAT TESTER
  // =============================================================================================

  combattest: (game) => {
    game.shortcuts.reset();
    game.shortcuts.open();
    game.shortcuts.show();

    game.shortcuts.addShortcutGroup({
      id: 'heroes',
      title: 'Hero',
      open: true,
    });
    game.shortcuts.addDropDown({
      group: 'heroes',
      field: 'herotest',
      title: 'Choose Hero',
      value: game.model.getHeroes().map((h) => h.id),
      onChange: (e) => {
        const scene = game.sceneController.getScene(KEYS.CombatTest);
        if (scene) scene.spawnHero(e);
      },
    });
    game.shortcuts.addDropDown({
      group: 'heroes',
      field: 'herotest',
      title: 'Equip Hero',
      value: ['axe', 'guitar', 'katana'],
      onChange: (e) => {
        const scene = game.sceneController.getScene(KEYS.CombatTest);
        if (scene) scene.equipHero(e);
      },
    });
    game.shortcuts.addToggle({
      group: 'heroes',
      field: 'godmode',
      title: 'God Mode',
      value: game.model.godMode,
      onChange: (e) => (game.model.godMode = e),
    });
    game.shortcuts.addShortcut({
      group: 'heroes',
      field: 'fullspecial',
      title: 'Fill Special',
      value: () => {
        const scene = game.sceneController.getScene(KEYS.CombatTest);
        scene._hero._specialPoints = 100;
      },
    });

    game.shortcuts.addShortcutGroup({
      id: 'enemies',
      title: 'Enemies',
      open: true,
    });
    const eselect = game.shortcuts.addDropDown({
      group: 'enemies',
      field: 'enemytest',
      title: 'Choose Enemy',
      value: game.model.getEnemies().map((e) => e.id),
      onChange: (e) => {},
    });
    game.shortcuts.addShortcut({
      group: 'enemies',
      field: 'spawnenemy',
      title: 'Spawn Enemy',
      value: () => {
        let e = eselect.getValue();
        if (e && e !== '') {
          const scene = game.sceneController.getScene(KEYS.CombatTest);
          if (Array.isArray(e)) e = e[0];
          if (scene) scene.spawnEnemy(e);
        }
      },
    });

    game.shortcuts.addShortcutGroup({
      id: 'bosses',
      title: 'Bosses',
      open: true,
    });
    const bselect = game.shortcuts.addDropDown({
      group: 'bosses',
      field: 'bosstest',
      title: 'Choose Boss',
      value: Object.keys(BOSS_MAP),
      onChange: (e) => {},
    });
    game.shortcuts.addShortcut({
      group: 'bosses',
      field: 'spawnboss',
      title: 'Spawn Boss',
      value: () => {
        let e = bselect.getValue();
        if (e && e !== '') {
          const scene = game.sceneController.getScene(KEYS.CombatTest);
          if (Array.isArray(e)) e = e[0];
          if (scene) scene.spawnBoss(e);
        }
      },
    });
    addExit(game);
  },
  exitTest(game) {
    game.showWelcome();
    show.default(game);
  },
};

// =============================================================================================
// OPTIONS
// =============================================================================================

const addPlayerOptions = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'player', title: 'Player', open: false });
  game.shortcuts.addShortcut({
    group: 'player',
    field: 'unlockcharacters',
    title: 'Unlock All Heroes',
    value: () => game.model.getHeroes().forEach((h) => game.player.unlockHero(h.id)),
  });
  game.shortcuts.addShortcut({
    group: 'player',
    field: 'addcoins',
    title: 'Add 100 Coins',
    value: () => game.player.adjustCoins(100),
  });
  game.shortcuts.addToggle({
    group: 'player',
    field: 'godmode',
    title: 'God Mode',
    value: game.model.godMode,
    onChange: (e) => (game.model.godMode = e),
  });
};

// =============================================================================================
// IN GAME OPTIONS
// =============================================================================================

const addInGameOptions = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'ingame', title: 'Hero', open: true });
  game.shortcuts.addShortcut({
    group: 'ingame',
    field: 'damagehero',
    title: 'Damage Hero',
    value: () => {
      const scene = game.sceneController.getScene(KEYS.GamePlay);
      scene._hero.damage(25);
    },
  });
  game.shortcuts.addShortcut({
    group: 'ingame',
    field: 'killhero',
    title: 'Kill Hero',
    value: () => {
      const scene = game.sceneController.getScene(KEYS.GamePlay);
      scene._hero.damage(scene._hero.health);
    },
  });
  game.shortcuts.addShortcut({
    group: 'ingame',
    field: 'winstage',
    title: 'Win Stage',
    value: () => game.completeStage(),
  });
  game.shortcuts.addShortcut({
    group: 'ingame',
    field: 'failstage',
    title: 'Fail Stage',
    value: () => game.failStage(),
  });
  game.shortcuts.addDropDown({
    group: 'ingame',
    field: 'equiphero',
    title: 'Equip Hero',
    value: game.model.getWeaponIds(),
    onChange: (e) => {
      const scene = game.sceneController.getScene(KEYS.GamePlay);
      if (scene) scene.equipHero(e);
    },
  });
};

// =============================================================================================
// JUMP TO STAGE
// =============================================================================================

const addStageStart = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'stages', title: 'Stages', open: false });
  game.shortcuts.addDropDown({
    group: 'stages',
    field: 'playstage',
    title: 'play stage',
    value: game.model.getStoryStages().map((s) => s.id),
    onChange: (e) => game.testStage({ stageid: e }),
  });
  game.shortcuts.addShortcut({
    group: 'stages',
    field: 'gamecomplete',
    title: 'Complete Story',
    value: () => {
      if (game.player.story) game.player.story.forceCompleteStory();
      game.showStoryComplete();
    },
  });
};

// =============================================================================================
// LAUNCH TESTS
// =============================================================================================

const addLaunchTests = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'testing', title: 'Testing', open: false });
  game.shortcuts.addShortcut({
    group: 'testing',
    field: 'viewenemies',
    title: 'View Characters',
    value: () => game.showEnemyViewer(),
  });

  game.shortcuts.addShortcut({
    group: 'testing',
    field: 'combattest',
    title: 'Combat Tester',
    value: () => game.sceneController.switchScene(KEYS.CombatTest, {}),
  });

  game.shortcuts.addDropDown({
    group: 'testing',
    field: 'cutsceneviewer',
    title: 'View Cutscene',
    value: Object.keys(CUTSCENES),
    onChange: (e) => {
      game.sceneController.switchScene(KEYS.CutScene, {
        data: {
          type: e,
          onComplete: game.showWelcome.bind(game),
        },
      });
    },
  });
};

// =============================================================================================
// DEBUG DISPLAYS
// =============================================================================================

const addDebug = (game) => {
  game.shortcuts.addShortcutGroup({
    id: 'debug',
    title: 'Debug',
    open: false,
  });
  game.shortcuts.addToggle({
    group: 'debug',
    field: 'debug',
    title: 'Show Debug',
    value: game.model.showDebug,
    onChange: (e) => (game.model.showDebug = e),
  });
  game.shortcuts.addToggle({
    group: 'debug',
    field: 'debug',
    title: 'Colliders',
    value: game.model._showDebugCollisionColliders,
    onChange: (e) => (game.model._showDebugCollisionColliders = e),
  });
  game.shortcuts.addToggle({
    group: 'debug',
    field: 'debug',
    title: 'Collision Map',
    value: game.model._showDebugCollisionMap,
    onChange: (e) => (game.model._showDebugCollisionMap = e),
  });
  game.shortcuts.addToggle({
    group: 'debug',
    field: 'debug',
    title: 'Character States',
    value: game.model._showDebugCharacterStates,
    onChange: (e) => (game.model._showDebugCharacterStates = e),
  });
};

// =============================================================================================
// EXIT TO WELCOME
// =============================================================================================

const addExit = (game) => {
  game.shortcuts.addShortcutGroup({
    id: 'exit',
    title: 'Exit',
    open: false,
  });
  game.shortcuts.addShortcut({
    group: 'exit',
    field: 'exit',
    title: 'Exit',
    value: () => show.exitTest(game),
  });
};

// =============================================================================================
// MAP UPLOAD
// =============================================================================================

const addMapUploader = (game) => {
  game.shortcuts.addShortcutGroup({
    id: 'upload',
    title: 'Upload',
    open: false,
  });
  game.shortcuts.addShortcut({
    group: 'upload',
    field: 'uploadmap',
    title: 'Upload Map',
    value: () => {
      if (!game.shortcuts.upload) {
        game.shortcuts.upload = document.createElement('input');
        game.shortcuts.upload.setAttribute('type', 'file');
        game.shortcuts.upload.setAttribute('accept', '.json');
        game.shortcuts.upload.setAttribute('style', 'visibility:hidden');
      }
      game.shortcuts.upload.click();
      game.shortcuts.upload.addEventListener('change', (e) => {
        if (game.shortcuts.upload.files.length > 0) {
          var reader = new FileReader();
          reader.addEventListener('load', () => {
            var result = JSON.parse(reader.result);
            const name = game.shortcuts.upload.files[0].name.replace('.json', '');
            if (game.cache.json.exists(name)) game.cache.json.remove(name);

            const stage = game.model.getStoryStages().find((s) => s.mapid === name);
            if (!stage) {
              alert('map does not belong to any stage, please upload a stage map');
            } else {
              if (game.cache.json.exists(name)) game.cache.json.remove(name);
              game.cache.json.add(name, result);
              game.testStage({ stageid: stage.id });
            }
          });
          reader.readAsText(game.shortcuts.upload.files[0]);
        }
      });
    },
  });
};

// =============================================================================================
// RENDERING OPTIONS
// =============================================================================================

const addRendererOptions = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'renderer', title: 'Renderer', open: false });
  if (game.renderer.type === 2) {
    game.shortcuts.addShortcut({
      group: 'renderer',
      field: 'renderer',
      title: 'Use Canvas Mode',
      value: () => {
        var params = new URLSearchParams(window.location.search);
        params.set('shortcuts', true);
        params.set('renderer', 'canvas');
        window.location.search = params.toString();
      },
    });
  } else {
    game.shortcuts.addShortcut({
      group: 'renderer',
      field: 'renderer',
      title: 'Use WebGL Mode',
      value: () => {
        var params = new URLSearchParams(window.location.search);
        params.set('shortcuts', true);
        params.set('renderer', 'webgl');
        window.location.search = params.toString();
      },
    });
  }
};

// =============================================================================================
// RENDERING OPTIONS
// =============================================================================================

const addLocaleOptions = (game) => {
  game.shortcuts.addShortcutGroup({ id: 'locale', title: 'Locale', open: false });
  game.shortcuts.addDropDown({
    group: 'locale',
    field: 'locale',
    title: 'Language',
    value: SUPPORTED_LOCALES,
    onChange: (e) => {
      var params = new URLSearchParams(window.location.search);
      params.set('shortcuts', true);
      params.set('locale', e);
      window.location.search = params.toString();
    },
  });
};
